import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import {
  type CreateSessionInFlowMutationBody,
  postFlowMutationWithConflictRetry,
} from '@/state/mutations/flows/apply-flow-mutation.http';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow';
import { GET_EVENT_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-event-sessions';
import { GET_EVENT_FILTERED_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-filtered-event-sessions';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';

export type CreateSessionInFlowParams = {
  flowId: string;
  body: CreateSessionInFlowMutationBody;
  flowQueryKey?: readonly unknown[];
  idempotencyKey?: string;
};

export function useCreateSessionInFlowMutation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ flowId, body, flowQueryKey, idempotencyKey }: CreateSessionInFlowParams) => {
      const token = await getToken();
      return postFlowMutationWithConflictRetry({
        flowId,
        token: token ?? '',
        queryClient,
        flowQueryKey,
        idempotencyKey,
        buildBody: (serverFlow) => ({
          ...body,
          baseVersion: serverFlow?.version ?? body.baseVersion,
        }),
      });
    },
    onSuccess: (flow: FlowDto) => {
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: [GET_FLOW_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_SESSIONS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_FILTERED_SESSIONS_QUERY_KEY] }),
      ]).then(() => flow);
    },
  });
}
